import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import classNames from 'classnames';
import moment from 'moment';

import './swap-shift.css';

import { swapShiftValidations } from './validations';
import {
  baseDateFormat,
  defaultDateFormatting,
} from '../../../../../../common/helper';

import { useTranslate, useValidation } from '../../../../../../hooks';
import { useSwapShift } from './api';
import { useMyShiftRoster } from '../api';

import {
  EmpShiftData,
  EmpShiftObj,
  HandleSelectShiftType,
  SelectedEmpType,
} from './types';

import {
  AssignedEmployeeShiftList,
  CurrentEmployeeShiftList,
  ShiftEmployeeList,
} from './components';
import { Button, LoadingButton } from '../../../../../../components/buttons';
import { Backdrop } from '../../../../../../components';
import KeyboardBackspaceIcon from '@mui/icons-material/KeyboardBackspace';

function resolveForm(
  firstEmployeeDates: EmpShiftData,
  secondEmployeeDates: EmpShiftData = {},
) {
  const firstEmployeeShiftDates = Object.values(firstEmployeeDates).map(
    (data: any) => data?.date?.date,
  );

  const secondEmployeeShiftDates = Object.values(secondEmployeeDates).map(
    (data: any) => data?.date?.date,
  );

  const firstEmployeeShiftCodes = Object.values(firstEmployeeDates).map(
    (data: any) => data?.code?.code,
  );

  const secondEmployeeShiftCodes = Object.values(secondEmployeeDates).map(
    (data: any) => data?.code?.code,
  );

  const firstEmployeeShiftsDetails = Object.values(firstEmployeeDates).map(
    (data: any) => data?.shiftTime,
  );

  const secondEmployeeShiftsDetails = Object.values(secondEmployeeDates).map(
    (data: any) => data?.shiftTime,
  );

  return {
    firstEmployeeShiftDates,
    secondEmployeeShiftDates,
    firstEmployeeShiftCodes,
    secondEmployeeShiftCodes,
    firstEmployeeShiftsDetails,
    secondEmployeeShiftsDetails,
  };
}

const DAYS = 20;

function SwapShift2() {
  const [search] = useSearchParams();
  const navigate = useNavigate();
  const [selectedShifts, setSelectedShifts] = useState<EmpShiftObj>({
    currentEmp: {},
    assignedEmp: {},
  });
  const [selectedEmployee, setSelectedEmployee] = useState<SelectedEmpType>({});

  const { errors, validate } = useValidation(swapShiftValidations, {
    secondEmployeeId: selectedEmployee?.id,
    ...resolveForm(selectedShifts?.currentEmp, selectedShifts.assignedEmp),
  });

  const { data: list, isLoading } = useMyShiftRoster({
    params: {
      startDate: baseDateFormat(
        search.get('start-date'),
        defaultDateFormatting,
      ),
      endDate: baseDateFormat(
        moment(search.get('start-date')).add(DAYS, 'days'),
        defaultDateFormatting,
      ),
    },
  });

  const { isLoading: isSwappingShift, swapShift } = useSwapShift();

  const { translate, isRTL } = useTranslate();

  const handleEmployeeSelect = (employeeDetails: SelectedEmpType) => {
    setSelectedShifts((prev) => ({ ...prev, assignedEmp: {} }));
    setSelectedEmployee(employeeDetails);
  };

  const handleCurrentEmpShiftSelect = ({ shifts }: HandleSelectShiftType) => {
    setSelectedShifts((prev: EmpShiftObj) => ({
      ...prev,
      currentEmp: { ...shifts },
    }));
  };

  const handleAssignedEmpShiftSelect = ({ shifts }: HandleSelectShiftType) => {
    setSelectedShifts((prev: EmpShiftObj) => ({
      ...prev,
      assignedEmp: { ...shifts },
    }));
  };

  const getSelectedShift = (key: string) =>
    Object.keys(selectedShifts?.[key]).map((key: any) => Number(key));

  const onClickSave = async () => {
    if (await validate()) {
      await swapShift({
        employeeId: search.get('id'),
        swappedWithId: selectedEmployee?.id,
        dates: resolveForm(selectedShifts?.currentEmp).firstEmployeeShiftDates,
      });

      navigate(-1);
    }
  };

  return (
    <>
      <Backdrop open={isLoading} />
      <div className="swap-shift-contaier">
        <span className="swap-shift-title-section">
          <KeyboardBackspaceIcon
            className={classNames('swap-shift-back-btn', {
              rotate180: isRTL,
            })}
            onClick={() => navigate(-1)}
          />
          <p className="swap-shift-title-text">{translate('Swap')}</p>
          <p className="swap-shift-title-text-2">{translate('Shift')}</p>
        </span>
        <div className="swap-shift-body">
          <div className="emp-1-shift-container">
            <div className="emp-1-shift">
              <CurrentEmployeeShiftList
                data={list}
                days={DAYS}
                startDate={search.get('start-date')}
                selectedShifts={getSelectedShift('currentEmp')}
                handleSelectShift={handleCurrentEmpShiftSelect}
              />
            </div>

            <div className="swap-shift-error">
              {errors['firstEmployeeShiftDates']}
            </div>
          </div>
          <div className="swap-icon-container">
            <i className="las la-exchange-alt swap-icon"></i>
          </div>
          <div className="emplist-container">
            <div className="emplist">
              <ShiftEmployeeList
                handleEmployeeSelect={handleEmployeeSelect}
                firstEmployeeId={search.get('id')}
              />
            </div>
            {errors['secondEmployeeId'] && (
              <div className="swap-shift-error">
                {errors['secondEmployeeId']}
              </div>
            )}
          </div>
          <div className="emp-2-shift-container">
            <div className="emp-2-shift">
              <AssignedEmployeeShiftList
                selectedEmployee={selectedEmployee}
                days={DAYS}
                startDate={search.get('start-date')}
                selectedShifts={getSelectedShift('assignedEmp')}
                handleSelectShift={handleAssignedEmpShiftSelect}
              />
            </div>

            <div className="swap-shift-error">
              {errors['secondEmployeeShiftDates']}
            </div>
          </div>
        </div>

        <div className="swap-action">
          <Button
            className="outline-btn global-btn"
            onClick={() => {
              navigate(-1);
            }}
          >
            {translate('Cancel')}
          </Button>
          <LoadingButton
            className="primary-btn global-btn"
            onClick={onClickSave}
            isLoading={isSwappingShift}
          >
            {translate('Save')}
          </LoadingButton>
        </div>
      </div>
    </>
  );
}

export default SwapShift2;
